import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, SlidersHorizontal, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getVehicles } from '../services/api';

export const Inventory = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [makeFilter, setMakeFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    const loadVehicles = async () => {
      try {
        const response = await getVehicles();
        setVehicles(response.data);
      } catch (error) {
        console.error('Error loading vehicles:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadVehicles();
  }, []);

  const uniqueMakes = ['all', ...new Set(vehicles.map(v => v.make))];

  const filteredVehicles = useMemo(() => {
    let filtered = vehicles.filter(vehicle => {
      const matchesSearch = 
        vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.year.toString().includes(searchTerm);
      
      const matchesMake = makeFilter === 'all' || vehicle.make === makeFilter;
      
      let matchesPrice = true;
      if (priceFilter === 'under30k') matchesPrice = vehicle.price < 30000;
      else if (priceFilter === '30to40k') matchesPrice = vehicle.price >= 30000 && vehicle.price < 40000;
      else if (priceFilter === '40to50k') matchesPrice = vehicle.price >= 40000 && vehicle.price < 50000;
      else if (priceFilter === 'over50k') matchesPrice = vehicle.price >= 50000;

      return matchesSearch && matchesMake && matchesPrice;
    });

    // Sort
    if (sortBy === 'newest') {
      filtered.sort((a, b) => b.year - a.year);
    } else if (sortBy === 'price-low') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'mileage') {
      filtered.sort((a, b) => a.mileage - b.mileage);
    }

    return filtered;
  }, [vehicles, searchTerm, makeFilter, priceFilter, sortBy]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-white py-12 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Inventory
          </h1>
          <p className="text-xl text-gray-600">
            {loading ? 'Loading vehicles...' : `Browse our selection of ${vehicles.length} quality luxury vehicles`}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white py-6 sticky top-20 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search by make, model, or year..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Make Filter */}
            <Select value={makeFilter} onValueChange={setMakeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Makes" />
              </SelectTrigger>
              <SelectContent>
                {uniqueMakes.map(make => (
                  <SelectItem key={make} value={make}>
                    {make === 'all' ? 'All Makes' : make}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Price Filter */}
            <Select value={priceFilter} onValueChange={setPriceFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Prices" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Prices</SelectItem>
                <SelectItem value="under30k">Under $30,000</SelectItem>
                <SelectItem value="30to40k">$30,000 - $40,000</SelectItem>
                <SelectItem value="40to50k">$40,000 - $50,000</SelectItem>
                <SelectItem value="over50k">Over $50,000</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="mileage">Lowest Mileage</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 text-gray-600">
            Showing {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
          </div>

          {filteredVehicles.length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-xl text-gray-600 mb-4">No vehicles found matching your criteria</p>
              <Button onClick={() => {
                setSearchTerm('');
                setMakeFilter('all');
                setPriceFilter('all');
              }}>
                Clear Filters
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredVehicles.map((vehicle) => (
                <Card key={vehicle.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="aspect-video overflow-hidden relative">
                    <img 
                      src={vehicle.images[0]} 
                      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 right-4">
                      <span className="bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {vehicle.condition}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold text-gray-900">
                        {vehicle.year} {vehicle.make}
                      </h3>
                      <span className="text-2xl font-bold text-teal-600">
                        ${vehicle.price.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4">{vehicle.model}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Mileage:</span>
                        <span className="font-semibold text-gray-700">{vehicle.mileage.toLocaleString()} miles</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Transmission:</span>
                        <span className="font-semibold text-gray-700">{vehicle.transmission}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Fuel:</span>
                        <span className="font-semibold text-gray-700">{vehicle.fuelType}</span>
                      </div>
                    </div>

                    <Link to={`/vehicle/${vehicle.id}`}>
                      <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                        View Details
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
